import {
  SelectionList,
  SelectionListProps,
  Text,
} from '@dixa/lib-shared-habla';
import { useMemo } from 'react';

import { CategorizedListProps } from 'components/categorizedList';
import {
  CategorizedListCategory,
  CategorizedListCategoryNamedId,
} from 'components/categorizedList/categorizedList.types';
import { categoryTitles } from 'components/categorizedList/utils/categories';

type CategorizedListMenuProps<TItem> = Pick<
  CategorizedListProps<TItem>,
  'categories'
> &
  Pick<SelectionListProps<CategorizedListCategory<TItem>>, 'onChange'> & {
    /** The visible category ids. */
    visibleCategoryIds: readonly CategorizedListCategoryNamedId[];
  };

/** Represents a categories menu. */
export const CategorizedListMenu = <TItem,>({
  categories,
  visibleCategoryIds,
  onChange,
}: CategorizedListMenuProps<TItem>): JSX.Element => {
  /**
   * The single first visible menu category that goes into the selection list.
   * This has to be an array as this is what the list expects.
   */
  const menuCategory = useMemo<CategorizedListCategory<TItem>[]>(() => {
    const firstVisibleCategory = categories.find((category) =>
      visibleCategoryIds.some((id) => id === category.id),
    );

    return firstVisibleCategory ? [firstVisibleCategory] : [];
  }, [categories, visibleCategoryIds]);

  return (
    <SelectionList<CategorizedListCategory<TItem>>
      selectedValues={menuCategory}
      onChange={onChange}
      equalityFn={(a, b) => a.id === b.id}
    >
      {categories.map((category) => (
        <SelectionList.Item key={category.id} value={category}>
          <Text.Paragraph variant="3">
            {categoryTitles[category.id]}
          </Text.Paragraph>
        </SelectionList.Item>
      ))}
    </SelectionList>
  );
};
